import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { applyTheme } from '@/lib/theme/applyTheme';
import { DEFAULT_THEME_ID, getTheme, THEMES } from '@/lib/theme/themes';
import type { Theme } from '@/lib/theme/tokens';

interface ThemeState {
  themeId: string;
  /**
   * A theme being previewed but not committed. The settings picker sets this
   * on hover/focus so the whole app repaints live; leaving the picker clears
   * it. Never persisted — a preview that survived a reload would be a bug.
   */
  previewThemeId: string | null;
  setTheme: (id: string) => void;
  previewTheme: (id: string | null) => void;
  /** The theme currently painted: preview if one is active, else the saved one. */
  activeTheme: () => Theme;
}

export const useThemeStore = create<ThemeState>()(
  conditionalDevtools(
    persist(
      (set, get) => ({
        themeId: DEFAULT_THEME_ID,
        previewThemeId: null,

        setTheme: (id) => {
          if (!THEMES.some((t) => t.id === id)) {
            return;
          }
          set({ themeId: id, previewThemeId: null });
          applyTheme(getTheme(id));
        },

        previewTheme: (id) => {
          if (id !== null && !THEMES.some((t) => t.id === id)) {
            return;
          }
          set({ previewThemeId: id });
          applyTheme(getTheme(id ?? get().themeId));
        },

        activeTheme: () => getTheme(get().previewThemeId ?? get().themeId),
      }),
      {
        name: 'theme-storage',
        version: 1,
        partialize: (state) => ({ themeId: state.themeId }),
        // The persisted id is applied here rather than in a component effect:
        // rehydration runs during store creation, which is before React's
        // first paint, so the saved theme is already on <html> and there is no
        // flash of the default palette.
        onRehydrateStorage: () => (state) => {
          applyTheme(getTheme(state?.themeId));
        },
      }
    ),
    { name: 'ThemeStore' }
  )
);

/**
 * Paint the stored theme.
 *
 * `onRehydrateStorage` already covers the normal path; this exists so
 * main.tsx can state the intent explicitly and so the call is idempotent if
 * storage was empty or unreadable (private mode, cleared site data).
 */
export function initTheme(): void {
  applyTheme(getTheme(useThemeStore.getState().themeId));
}
